import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { projectsAPI, taskGroupsAPI, tasksAPI, peopleAPI, revisionAPI, companiesAPI } from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowLeft, RefreshCw, Calendar, Clock, AlertTriangle, User, Users, ChevronRight, ChevronDown, CheckCircle2, AlertCircle, Pencil, Save, X, FileText, Loader2, RotateCcw, History, Lock, GitBranch, Link2 } from "lucide-react";
import { format, parseISO, differenceInDays, eachMonthOfInterval, addMonths, addDays } from "date-fns";
import { generateProjectReport, downloadReport } from "@/services/reportGenerator";
import { MultiSelect } from "@/components/ui/multi-select";
import { Checkbox } from "@/components/ui/checkbox";

function getFieldSafe(obj, key, def) {
  if (!obj) return def;
  var value = obj[key];
  if (value === undefined) return def;
  if (value === null) return def;
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    if (value.specialValue === 'NaN') return def;
    return def;
  }
  return value;
}

function formatDateSafe(str) {
  if (!str) return "-";
  try { return format(parseISO(str), "M/d/yyyy"); } 
  catch (e) { return "-"; }
}

function formatDateForInput(str) {
  if (!str) return "";
  try { return format(parseISO(str), "yyyy-MM-dd"); } 
  catch (e) { return ""; }
}

const PROJECT_STATUSES = ["Pre Project Planning", "Active", "On Hold", "Completed", "Archived", "Canceled"];
const TASK_STATUSES = ["Not Started", "In Progress", "Completed", "Overdue", "Revision Required", "Skipping"];
const TASK_GROUP_STATUSES = ["Not Started", "In Progress", "Complete"];
const REVISION_REQUESTERS = ["Customer", "OSR", "ISR", "Internal"];

const PROJECT_STATUS_COLORS = {
  "Active": "bg-indigo-500",
  "Completed": "bg-green-500",
  "On Hold": "bg-amber-500",
  "Archived": "bg-slate-500",
  "Canceled": "bg-red-600",
  "Cancelled": "bg-red-600",
  "Pre Project Planning": "bg-purple-500"
};

function ProjectDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const projectId = params.projectId;
  
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [taskGroups, setTaskGroups] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(projectId);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Edit states
  const [editingProject, setEditingProject] = useState(false);
  const [editingTaskGroup, setEditingTaskGroup] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [projectForm, setProjectForm] = useState({});
  const [taskGroupForm, setTaskGroupForm] = useState({});
  const [taskForm, setTaskForm] = useState({});
  
  // People data for dropdowns
  const [osrList, setOsrList] = useState([]);
  const [isrList, setIsrList] = useState([]);
  
  // Companies data for dropdown
  const [companies, setCompanies] = useState([]);
  
  // Report generation state
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Revision dialog state
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [revisionTask, setRevisionTask] = useState(null);
  const [revisionForm, setRevisionForm] = useState({ days_added: 5, reason: "", requested_by: "Customer" });
  const [submittingRevision, setSubmittingRevision] = useState(false);
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);
  const [revisionHistory, setRevisionHistory] = useState(null);

  // Parallel task selection state
  const [selectedTasks, setSelectedTasks] = useState({});
  const [selectionMode, setSelectionMode] = useState(false);

  // Dependencies state
  const [availablePredecessors, setAvailablePredecessors] = useState([]);
  const [loadingPredecessors, setLoadingPredecessors] = useState(false);
  const [dependencyImpactDialog, setDependencyImpactDialog] = useState({
    open: false,
    dependentTasks: [],
    pendingTaskUpdate: null
  });

  async function loadData() {
    setLoading(true);
    try {
      const curId = selectedId || projectId;
      
      // Fetch data in parallel - use getById for current project to get computed fields
      const [pRes, singlePRes, tgRes, tasksRes, peopleRes, companiesRes] = await Promise.all([
        projectsAPI.getAll(), 
        curId ? projectsAPI.getById(curId) : Promise.resolve({ data: { data: null } }),
        taskGroupsAPI.getAll(),
        tasksAPI.getAll(),
        peopleAPI.getAll(),
        companiesAPI.getAll()
      ]);
      const allP = pRes.data.data || [];
      setProjects(allP);
      setAllTasks(tasksRes.data.data || []);
      
      // Set people lists
      if (peopleRes.data.data) {
        setOsrList(peopleRes.data.data.osr || []);
        setIsrList(peopleRes.data.data.isr || []);
      }
      
      // Set companies list
      setCompanies(companiesRes.data?.data || []);
      
      // Use the single project response (with computed fields) if available
      const detailedProject = singlePRes.data?.data;
      const found = detailedProject || allP.find(function(p) { return p.id === curId; });
      setProject(found || null);
      if (found && found.fields) {
        const gIds = found.fields["Task Groups"] || [];
        const allG = tgRes.data.data || [];
        const filtered = allG.filter(function(g) { return gIds.includes(g.id); });
        filtered.sort(function(a, b) {
          var oA = a.fields ? (a.fields["Task Order"] || 0) : 0;
          var oB = b.fields ? (b.fields["Task Order"] || 0) : 0;
          return oA - oB;
        });
        setTaskGroups(filtered);
      } else {
        setTaskGroups([]);
      }
    } catch (e) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  }

  useEffect(function() { loadData(); }, [selectedId, projectId]);

  function onProjectChange(newId) {
    setSelectedId(newId);
    setExpandedGroups({});
    navigate("/project/" + newId, { replace: true });
  }

  function toggleGroup(groupId) {
    setExpandedGroups(function(prev) {
      return { ...prev, [groupId]: !prev[groupId] };
    });
  }

  function getTasksForGroup(groupId) {
    return allTasks.filter(function(task) {
      var taskGroupIds = task.fields ? (task.fields["Task Group"] || []) : [];
      return taskGroupIds.includes(groupId);
    }).sort(function(a, b) {
      var orderA = a.fields ? (a.fields["Task Order"] || 0) : 0;
      var orderB = b.fields ? (b.fields["Task Order"] || 0) : 0;
      return orderA - orderB;
    });
  }

  // Project editing
  function startEditProject() {
    const pf = project?.fields || {};
    const osrArray = getFieldSafe(pf, "OSR", []);
    const isrArray = getFieldSafe(pf, "ISR", []);
    const companyArray = getFieldSafe(pf, "Company Name", []);
    setProjectForm({
      name: getFieldSafe(pf, "Project Name", ""),
      description: getFieldSafe(pf, "Project Description", ""),
      status: getFieldSafe(pf, "Project Status", "Active"),
      start_date: formatDateForInput(getFieldSafe(pf, "Project Start Date", null)),
      end_date: formatDateForInput(getFieldSafe(pf, "Project End Date", null)),
      notes: getFieldSafe(pf, "Notes", ""),
      osr_ids: osrArray || [],
      isr_ids: isrArray || [],
      company_id: companyArray && companyArray.length > 0 ? companyArray[0] : ""
    });
    setEditingProject(true);
  }

  async function saveProject() {
    try {
      await projectsAPI.update(project.id, projectForm);
      toast.success("Project updated");
      setEditingProject(false);
      loadData();
    } catch (e) {
      toast.error("Failed to update project");
    }
  }

  // Task Group editing
  function startEditTaskGroup(group) {
    const gf = group.fields || {};
    setTaskGroupForm({
      name: getFieldSafe(gf, "Task Group Name", ""),
      start_date: formatDateForInput(getFieldSafe(gf, "Task Group Start Date", null)),
      due_date: formatDateForInput(getFieldSafe(gf, "Task Group Due Date", null)),
      status: getFieldSafe(gf, "Progress Status", "Not Started")
    });
    setEditingTaskGroup(group);
  }

  async function saveTaskGroup() {
    try {
      await taskGroupsAPI.update(editingTaskGroup.id, {
        name: taskGroupForm.name,
        start_date: taskGroupForm.start_date,
        due_date: taskGroupForm.due_date,
        status: taskGroupForm.status
      });
      toast.success("Task group updated");
      setEditingTaskGroup(null);
      loadData();
    } catch (e) {
      toast.error("Failed to update task group");
    }
  }

  // Task editing
  function startEditTask(task) {
    const tf = task.fields || {};
    setTaskForm({
      title: getFieldSafe(tf, "Task Name", ""),
      status: getFieldSafe(tf, "Status", "Not Started"),
      start_date: formatDateForInput(getFieldSafe(tf, "Start Date", null)),
      due_date: formatDateForInput(getFieldSafe(tf, "Due Date", null)),
      duration: getFieldSafe(tf, "Task Duration", 0),
      role: getFieldSafe(tf, "Role", ""),
      assigned_to: getFieldSafe(tf, "Assigned To", []),
      dates_locked: getFieldSafe(tf, "dates_locked", false),
      predecessors: getFieldSafe(tf, "predecessors", [])
    });
    setEditingTask(task);
    fetchTaskChangelog(task.id);
    fetchAvailablePredecessors(task.id, task.fields?.Projects?.[0]);
  }
  
  const [taskChangelog, setTaskChangelog] = useState([]);
  const [loadingChangelog, setLoadingChangelog] = useState(false);
  
  async function fetchTaskChangelog(taskId) {
    setLoadingChangelog(true);
    try {
      const res = await tasksAPI.getChangelog(taskId);
      setTaskChangelog(res.data?.data?.changes || []);
    } catch (e) {
      setTaskChangelog([]);
    }
    setLoadingChangelog(false);
  }

  async function fetchAvailablePredecessors(taskId, projectId) {
    setLoadingPredecessors(true);
    try {
      var res = await tasksAPI.getAvailablePredecessors(taskId, projectId);
      setAvailablePredecessors(res.data?.data?.available_predecessors || []);
    } catch (e) {
      console.error("Failed to fetch predecessors:", e);
      setAvailablePredecessors([]);
    }
    setLoadingPredecessors(false);
  }

  async function saveTask() {
    try {
      var originalTask = editingTask.fields || {};
      var datesChanged = 
        taskForm.start_date !== formatDateForInput(getFieldSafe(originalTask, "Start Date", null)) ||
        taskForm.due_date !== formatDateForInput(getFieldSafe(originalTask, "Due Date", null));
      
      if (datesChanged) {
        var impactRes = await tasksAPI.checkDependencyImpact(editingTask.id, []);
        var impact = impactRes.data?.data;
        
        if (impact?.has_dependents && impact?.tasks_needing_shift > 0) {
          setDependencyImpactDialog({
            open: true,
            dependentTasks: impact.dependent_tasks.filter(function(t) { return t.needs_shift; }),
            pendingTaskUpdate: { ...taskForm }
          });
          return;
        }
      }
      
      await tasksAPI.update(editingTask.id, taskForm);
      toast.success("Task updated");
      setEditingTask(null);
      loadData();
    } catch (e) {
      console.error("Save task error:", e);
      toast.error("Failed to update task");
    }
  }

  async function handleDependencyImpactConfirm(shiftDependents) {
    try {
      var pendingUpdate = dependencyImpactDialog.pendingTaskUpdate;
      await tasksAPI.update(editingTask.id, pendingUpdate);
      
      if (shiftDependents && dependencyImpactDialog.dependentTasks.length > 0) {
        var newDueDate = pendingUpdate.due_date;
        if (newDueDate) {
          var dueDate = new Date(newDueDate);
          dueDate.setDate(dueDate.getDate() + 1);
          while (dueDate.getDay() === 0 || dueDate.getDay() === 6) {
            dueDate.setDate(dueDate.getDate() + 1);
          }
          var newStartDate = dueDate.toISOString().split('T')[0];
          
          for (var i = 0; i < dependencyImpactDialog.dependentTasks.length; i++) {
            await tasksAPI.update(dependencyImpactDialog.dependentTasks[i].id, { start_date: newStartDate });
          }
          toast.success("Updated " + (dependencyImpactDialog.dependentTasks.length + 1) + " task(s)");
        }
      } else {
        toast.success("Task updated (dependent tasks unchanged)");
      }
      
      setDependencyImpactDialog({ open: false, dependentTasks: [], pendingTaskUpdate: null });
      setEditingTask(null);
      loadData();
    } catch (e) {
      console.error("Dependency impact confirm error:", e);
      toast.error("Failed to update tasks");
    }
  }

  // Quick status update for tasks
  async function quickUpdateTaskStatus(task, newStatus) {
    try {
      await tasksAPI.update(task.id, { status: newStatus });
      toast.success("Status updated");
      loadData();
    } catch (e) {
      toast.error("Failed to update status");
    }
  }

  // Parallel group management
  function toggleTaskSelection(taskId, e) {
    if (e) e.stopPropagation();
    setSelectedTasks(function(prev) { return { ...prev, [taskId]: !prev[taskId] }; });
  }

  function getSelectedTaskIds() {
    return Object.entries(selectedTasks).filter(function(entry) { return entry[1]; }).map(function(entry) { return entry[0]; });
  }

  function clearSelection() {
    setSelectedTasks({});
    setSelectionMode(false);
  }

  async function createParallelGroup() {
    var taskIds = getSelectedTaskIds();
    if (taskIds.length < 2) {
      toast.error("Select at least 2 tasks to create a parallel group");
      return;
    }
    try {
      await tasksAPI.createParallelGroup(taskIds);
      toast.success("Created parallel group with " + taskIds.length + " tasks");
      clearSelection();
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create parallel group");
    }
  }

  async function removeFromParallelGroup() {
    var taskIds = getSelectedTaskIds();
    if (taskIds.length === 0) {
      toast.error("Select at least 1 task to remove from parallel group");
      return;
    }
    try {
      await tasksAPI.removeParallelGroup(taskIds);
      toast.success("Removed tasks from parallel group");
      clearSelection();
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove from parallel group");
    }
  }

  if (loading) {
    return React.createElement("div", { className: "space-y-6" },
      React.createElement(Skeleton, { className: "h-10 w-64" }),
      React.createElement("div", { className: "grid grid-cols-4 gap-4" },
        [1,2,3,4].map(function(i) { return React.createElement(Skeleton, { key: i, className: "h-24" }); })
      ),
      React.createElement(Skeleton, { className: "h-96" })
    );
  }

  // Generate PDF Report
  async function handleGenerateReport() {
    if (!project) return;
    setGeneratingReport(true);
    try {
      const response = await projectsAPI.getReportData(selectedId || projectId);
      if (response.data.success) {
        const reportData = response.data.data;
        const doc = await generateProjectReport(reportData, '/images/gamer-logo.jpg');
        const filename = `${reportData.project.name}-${reportData.project.customer || 'Report'} ${format(new Date(), 'MMddyyyy')}.pdf`;
        downloadReport(doc, filename);
        toast.success("Report downloaded successfully!");
      } else {
        toast.error("Failed to generate report");
      }
    } catch (error) {
      console.error("Report generation error:", error);
      toast.error("Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  }

  // Revision functions
  function openRevisionDialog(task) {
    setRevisionTask(task);
    setRevisionForm({ days_added: 5, reason: "", requested_by: "Customer" });
    setShowRevisionDialog(true);
  }

  async function submitRevision() {
    if (!revisionTask || !revisionForm.reason.trim()) {
      toast.error("Please provide a reason for the revision");
      return;
    }
    
    setSubmittingRevision(true);
    try {
      const response = await revisionAPI.request(revisionTask.id, revisionForm);
      if (response.data.success) {
        const result = response.data.data;
        toast.success(
          `Revision #${result.revision_number} added: +${result.days_added} days. ` +
          (result.cascaded_tasks.length > 0 ? `${result.cascaded_tasks.length} dependent task(s) updated.` : "")
        );
        setShowRevisionDialog(false);
        loadData();
      }
    } catch (error) {
      console.error("Revision error:", error);
      toast.error("Failed to request revision");
    } finally {
      setSubmittingRevision(false);
    }
  }

  async function viewRevisionHistory(task) {
    try {
      const response = await revisionAPI.getHistory(task.id);
      if (response.data.success) {
        setRevisionHistory(response.data.data);
        setShowRevisionHistory(true);
      }
    } catch (error) {
      toast.error("Failed to load revision history");
    }
  }

  var pf = project ? project.fields : {};
  var title = getFieldSafe(pf, "Full Project Title", "") || getFieldSafe(pf, "Project Name", "Project");
  var customerName = (getFieldSafe(pf, "Customer (from Company Name)", []) || [])[0] || "";
  var startD = getFieldSafe(pf, "Project Start Date", null);
  var endD = getFieldSafe(pf, "Project End Date", null);
  var duration = getFieldSafe(pf, "Project Duration", "-");
  var status = getFieldSafe(pf, "Project Status", "Unknown");
  var schedStatus = getFieldSafe(pf, "Schedule Status", "Unknown");
  var osrText = getFieldSafe(pf, "OSR(Text)", "-");
  var isrText = getFieldSafe(pf, "ISR(Text)", "-").replace(/"/g, "").replace(/\n/g, ", ");
  var completePct = (getFieldSafe(pf, "% of project completed", 0)) * 100;

  // Use API-calculated schedule values
  var behindDays = getFieldSafe(pf, "Days Behind", 0);
  var aheadDays = getFieldSafe(pf, "Days Ahead", 0);
  var expectedProgress = getFieldSafe(pf, "Expected Progress", 0);
  var scheduleMessage = getFieldSafe(pf, "Schedule Message", "");

  var months = [];
  var timeStart = new Date();
  var timeEnd = new Date();
  if (startD && endD) {
    try {
      timeStart = parseISO(startD);
      timeEnd = parseISO(endD);
      months = eachMonthOfInterval({ start: timeStart, end: addMonths(timeEnd, 1) });
    } catch (e) {}
  }

  // Calculate today's position on timeline (for red "current" marker)
  function calcTodayPosition() {
    if (months.length === 0 || !startD || !endD) return null;
    try {
      var today = new Date();
      var total = differenceInDays(timeEnd, timeStart) + 30;
      var offset = differenceInDays(today, timeStart);
      var pos = (offset / total) * 100;
      if (pos < 0 || pos > 100) return null;
      return pos + "%";
    } catch (e) { return null; }
  }

  // Calculate expected position (where we should be if on schedule - for green marker)
  function calcExpectedPosition() {
    if (months.length === 0 || !startD || !endD || expectedProgress <= 0) return null;
    try {
      var totalDuration = differenceInDays(timeEnd, timeStart);
      var expectedDays = (expectedProgress / 100) * totalDuration;
      var expectedDate = addDays(timeStart, expectedDays);
      var total = differenceInDays(timeEnd, timeStart) + 30;
      var offset = differenceInDays(expectedDate, timeStart);
      var pos = (offset / total) * 100;
      if (pos < 0 || pos > 100) return null;
      return pos + "%";
    } catch (e) { return null; }
  }

  var todayPos = calcTodayPosition();
  var expectedPos = calcExpectedPosition();

  function calcBar(s, e) {
    if (!s || months.length === 0) return null;
    try {
      var ts = parseISO(s);
      var te = e ? parseISO(e) : ts;
      var total = differenceInDays(timeEnd, timeStart) + 30;
      var offset = differenceInDays(ts, timeStart);
      var dur = differenceInDays(te, ts) + 1;
      var left = Math.max(0, (offset / total) * 100);
      var width = Math.min((dur / total) * 100, 100 - left);
      return { left: left + "%", width: Math.max(width, 2) + "%" };
    } catch (e) { return null; }
  }

  var statusColors = {
    "Not Started": { bg: "bg-slate-100", text: "text-slate-600", bar: "#94a3b8" },
    "In Progress": { bg: "bg-blue-100", text: "text-blue-600", bar: "#3b82f6" },
    "Complete": { bg: "bg-green-100", text: "text-green-600", bar: "#10b981" },
    "Completed": { bg: "bg-green-100", text: "text-green-600", bar: "#10b981" }
  };

  var taskStatusColors = {
    "Not Started": "bg-slate-500",
    "In Progress": "bg-blue-500",
    "Completed": "bg-green-500",
    "Overdue": "bg-red-500",
    "Revision Required": "bg-amber-500",
    "Skipping": "bg-gray-400"
  };

  function getStatCfg(st) { return statusColors[st] || statusColors["Not Started"]; }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={function() { navigate("/projects"); }} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <h1 className="text-lg md:text-xl font-bold text-primary">{title}</h1>
            {customerName && <span className="text-muted-foreground text-sm md:text-base">— {customerName}</span>}
          </div>
          {project && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="xs" className="h-7 px-2 text-xs" onClick={startEditProject} data-testid="edit-project-btn">
                <Pencil className="h-3 w-3 mr-1" /> Edit
              </Button>
              <Button 
                variant="outline" 
                size="xs"
                className="h-7 px-2 text-xs"
                onClick={handleGenerateReport}
                disabled={generatingReport}
                data-testid="generate-report-btn"
              >
                {generatingReport ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <FileText className="h-3 w-3 mr-1" />
                )}
                {generatingReport ? "..." : "Report"}
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedId || projectId} onValueChange={onProjectChange}>
            <SelectTrigger className="w-64" data-testid="project-select">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {projects.map(function(p) {
                var nm = p.fields ? (p.fields["Project Name"] || "Unnamed") : "Unnamed";
                return <SelectItem key={p.id} value={p.id}>{nm}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={loadData}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {project ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Calendar className="h-3 w-3" />Project Start</div>
              <p className="text-lg font-semibold">{formatDateSafe(startD)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Calendar className="h-3 w-3" />Project End</div>
              <p className="text-lg font-semibold">{formatDateSafe(endD)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Clock className="h-3 w-3" />Duration</div>
              <p className="text-lg font-semibold">{duration} days</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-4">
              <div className="text-muted-foreground text-xs mb-1">Status</div>
              <Badge className={status === "Active" ? "bg-green-500" : "bg-slate-500"}>{status}</Badge>
            </CardContent></Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2"><AlertTriangle className="h-3 w-3" />Schedule</div>
              <div className="flex items-center gap-3">
                <Badge className={schedStatus === "Behind Schedule" ? "bg-red-500" : schedStatus === "Ahead of Schedule" ? "bg-blue-500" : schedStatus === "On Schedule" ? "bg-green-500" : schedStatus === "Completed" ? "bg-green-600" : "bg-slate-500"}>{schedStatus}</Badge>
                {schedStatus === "Behind Schedule" && <span className="text-red-500 font-semibold text-sm">{behindDays} days behind</span>}
                {schedStatus === "Ahead of Schedule" && <span className="text-blue-500 font-semibold text-sm">{aheadDays} days ahead</span>}
              </div>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><User className="h-3 w-3" />OSR</div>
              <p className="text-sm font-medium">{osrText}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Users className="h-3 w-3" />ISR</div>
              <p className="text-sm font-medium">{isrText}</p>
            </CardContent></Card>
          </div>

          <Card><CardContent className="pt-4 pb-4">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium">% Completed</span>
              <span className="text-sm font-semibold">{completePct.toFixed(2)}%</span>
            </div>
            <div className="relative h-6 bg-muted rounded-full overflow-hidden">
              <div className="absolute left-0 top-0 h-full bg-primary" style={{ width: completePct + "%" }} />
              <div className="absolute inset-0 flex items-center justify-between px-3 text-xs">
                <span>0%</span><span className="font-medium">{completePct.toFixed(2)}%</span><span>100%</span>
              </div>
            </div>
          </CardContent></Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Task Groups</CardTitle>
              <p className="text-sm text-muted-foreground">Click to expand • Click edit icon to modify</p>
            </CardHeader>
            <CardContent>
              {taskGroups.length > 0 ? (
                <div className="space-y-2">
                  {taskGroups.map(function(g) {
                    var gf = g.fields || {};
                    var gn = getFieldSafe(gf, "Task Group Name", "Unnamed");
                    var gs = getFieldSafe(gf, "Task Group Start Date", null);
                    var ge = getFieldSafe(gf, "Task Group Due Date", null);
                    var gp = (getFieldSafe(gf, "Progress %", 0)) * 100;
                    var gst = getFieldSafe(gf, "Progress Status", "Not Started");
                    var cfg = getStatCfg(gst);
                    var isExpanded = expandedGroups[g.id];
                    var groupTasks = getTasksForGroup(g.id);

                    return (
                      <Collapsible key={g.id} open={isExpanded} onOpenChange={function() { toggleGroup(g.id); }}>
                        <div className="border rounded-lg overflow-hidden">
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                              <div className="flex items-center gap-3">
                                {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                <span className="font-medium">{gn}</span>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6"
                                  onClick={function(e) { e.stopPropagation(); startEditTaskGroup(g); }}
                                  data-testid={"edit-taskgroup-" + g.id}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                                  <span>{formatDateSafe(gs)}</span>
                                  <span>→</span>
                                  <span>{formatDateSafe(ge)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-2 bg-muted rounded-full">
                                    <div className="h-full rounded-full" style={{ width: gp + "%", backgroundColor: cfg.bar }} />
                                  </div>
                                  <span className="text-sm w-12">{gp.toFixed(0)}%</span>
                                </div>
                                <Badge className={cfg.bg + " " + cfg.text}>{gst}</Badge>
                                <Badge variant="secondary">{groupTasks.length} tasks</Badge>
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="border-t bg-muted/20">
                              {groupTasks.length > 0 ? (
                                <div className="p-3">
                                  {/* Selection toolbar */}
                                  {selectionMode && getSelectedTaskIds().length > 0 && (
                                    <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                        {getSelectedTaskIds().length} selected
                                      </span>
                                      <div className="flex-1" />
                                      <Button size="sm" variant="outline" onClick={createParallelGroup} disabled={getSelectedTaskIds().length < 2} data-testid="make-parallel-btn">
                                        <GitBranch className="h-3 w-3 mr-1" /> Make Parallel
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={removeFromParallelGroup} data-testid="remove-parallel-btn">
                                        Remove Parallel
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={clearSelection}>
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                  {/* Selection mode toggle */}
                                  {!selectionMode && (
                                    <div className="flex justify-end mb-2">
                                      <Button size="sm" variant="ghost" onClick={function() { setSelectionMode(true); }} className="text-xs" data-testid="select-for-parallel-btn">
                                        <GitBranch className="h-3 w-3 mr-1" /> Select for Parallel
                                      </Button>
                                    </div>
                                  )}
                                  <Table>
                                  <TableHeader>
                                    <TableRow className="bg-muted/30">
                                      {selectionMode && <TableHead className="w-10"></TableHead>}
                                      <TableHead className="w-12">#</TableHead>
                                      <TableHead>Task Name</TableHead>
                                      <TableHead>Assigned To</TableHead>
                                      <TableHead>Status</TableHead>
                                      <TableHead>Start Date</TableHead>
                                      <TableHead>Due Date</TableHead>
                                      <TableHead>Duration</TableHead>
                                      <TableHead>Role</TableHead>
                                      <TableHead className="w-16">Edit</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {(function() {
                                      var currentParallelGroup = null;
                                      var parallelGroupIndex = 0;
                                      var parallelGroupColors = ['border-l-purple-500', 'border-l-cyan-500', 'border-l-pink-500', 'border-l-orange-500'];
                                      
                                      return groupTasks.map(function(task) {
                                      var tf = task.fields || {};
                                      var rawStatus = getFieldSafe(tf, "Status", "Not Started");
                                      var isCompleted = rawStatus === "Completed" || rawStatus === "Complete" || rawStatus === "Skipping";
                                      var taskDue = getFieldSafe(tf, "Due Date", null);
                                      var computedOverdue = !isCompleted && taskDue && new Date(taskDue) < new Date(new Date().toDateString()) && rawStatus !== "Overdue" && rawStatus !== "Revision Required";
                                      var taskStatus = computedOverdue ? "Overdue" : rawStatus;
                                      var isOverdue = taskStatus === "Overdue";
                                      var isRevision = taskStatus === "Revision Required";
                                      var taskName = getFieldSafe(tf, "Task Name", "Unnamed Task");
                                      var taskOrder = getFieldSafe(tf, "Task Order", "-");
                                      var taskStart = getFieldSafe(tf, "Start Date", null);
                                      var taskDuration = getFieldSafe(tf, "Task Duration", 0);
                                      var taskRole = getFieldSafe(tf, "Role", "-");
                                      var revisions = getFieldSafe(tf, "revisions", []);
                                      var totalRevisionDays = getFieldSafe(tf, "total_revision_days", 0);
                                      var hasRevisions = revisions.length > 0;
                                      var assignedTo = getFieldSafe(tf, "Assigned To", []);
                                      var parallelGroup = getFieldSafe(tf, "parallel_group", null);
                                      var hasPredecessors = (getFieldSafe(tf, "predecessors", []) || []).length > 0;
                                      var isSelected = selectedTasks[task.id];
                                      
                                      // Track parallel group transitions
                                      if (parallelGroup !== currentParallelGroup) {
                                        if (parallelGroup) parallelGroupIndex++;
                                        currentParallelGroup = parallelGroup;
                                      }
                                      var parallelColor = parallelGroup ? parallelGroupColors[parallelGroupIndex % parallelGroupColors.length] : '';
                                      
                                      var assignedNames = assignedTo.map(function(id) {
                                        var osr = osrList.find(function(p) { return p.id === id; });
                                        if (osr) return osr.name;
                                        var isr = isrList.find(function(p) { return p.id === id; });
                                        if (isr) return isr.name;
                                        return null;
                                      }).filter(Boolean);

                                      return (
                                        <TableRow 
                                          key={task.id} 
                                          className={(isCompleted ? "opacity-60" : isRevision ? "bg-amber-50" : "") + (parallelGroup ? " border-l-4 " + parallelColor : "") + (isSelected ? " ring-2 ring-blue-500" : "") + " cursor-pointer hover:bg-muted/50 transition-colors"}
                                          onDoubleClick={function() { if (!selectionMode) startEditTask(task); }}
                                          onClick={function() { if (selectionMode) toggleTaskSelection(task.id); }}
                                          data-testid={"task-row-" + task.id}
                                        >
                                          {selectionMode && (
                                            <TableCell onClick={function(e) { e.stopPropagation(); }}>
                                              <Checkbox 
                                                checked={isSelected || false}
                                                onCheckedChange={function() { toggleTaskSelection(task.id); }}
                                              />
                                            </TableCell>
                                          )}
                                          <TableCell className="text-muted-foreground text-sm">{taskOrder}</TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-2">
                                              {parallelGroup && (
                                                <TooltipProvider>
                                                  <Tooltip>
                                                    <TooltipTrigger asChild>
                                                      <GitBranch className="h-4 w-4 text-purple-500 flex-shrink-0" />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                      <p>Parallel Group: {parallelGroup}</p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                </TooltipProvider>
                                              )}
                                              {hasPredecessors && (
                                                <TooltipProvider>
                                                  <Tooltip>
                                                    <TooltipTrigger asChild>
                                                      <Link2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                      <p>{getFieldSafe(tf, "predecessors", []).length} predecessor(s)</p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                </TooltipProvider>
                                              )}
                                              {getFieldSafe(tf, "dates_locked", false) && <Lock className="h-4 w-4 text-amber-500 flex-shrink-0" title="Dates locked" />}
                                              {isCompleted && <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />}
                                              {isOverdue && <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                                              {isRevision && <RotateCcw className="h-4 w-4 text-amber-500 flex-shrink-0" />}
                                              <span className={isCompleted ? "line-through" : ""}>{taskName}</span>
                                              {hasRevisions && (
                                                <Badge 
                                                  variant="outline" 
                                                  className="text-xs bg-amber-100 text-amber-700 cursor-pointer hover:bg-amber-200"
                                                  onClick={function(e) { e.stopPropagation(); viewRevisionHistory(task); }}
                                                >
                                                  {revisions.length} rev (+{totalRevisionDays}d)
                                                </Badge>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            {assignedNames.length > 0 ? (
                                              <div className="flex flex-wrap gap-1">
                                                {assignedNames.map(function(name, idx) {
                                                  return <Badge key={idx} variant="outline" className="text-xs">{name}</Badge>;
                                                })}
                                              </div>
                                            ) : (
                                              <span className="text-muted-foreground text-xs">Unassigned</span>
                                            )}
                                          </TableCell>
                                          <TableCell onClick={function(e) { e.stopPropagation(); }}>
                                            <Select 
                                              value={taskStatus} 
                                              onValueChange={function(val) { quickUpdateTaskStatus(task, val); }}
                                              disabled={selectionMode}
                                            >
                                              <SelectTrigger className="h-7 w-32">
                                                <Badge className={taskStatusColors[taskStatus] || "bg-slate-500"}>{taskStatus}</Badge>
                                              </SelectTrigger>
                                              <SelectContent>
                                                {TASK_STATUSES.map(function(s) {
                                                  return <SelectItem key={s} value={s}>{s}</SelectItem>;
                                                })}
                                              </SelectContent>
                                            </Select>
                                          </TableCell>
                                          <TableCell className="text-sm">{formatDateSafe(taskStart)}</TableCell>
                                          <TableCell className="text-sm">
                                            <div className="flex items-center gap-1">
                                              {isOverdue && <AlertCircle className="h-3 w-3 text-red-500" />}
                                              {formatDateSafe(taskDue)}
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-sm text-muted-foreground">{taskDuration} days</TableCell>
                                          <TableCell className="text-sm text-muted-foreground">{taskRole}</TableCell>
                                          <TableCell onClick={function(e) { e.stopPropagation(); }}>
                                            {!selectionMode && (
                                            <div className="flex items-center gap-1">
                                              <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-7 w-7"
                                                onClick={function() { openRevisionDialog(task); }}
                                                title="Request Revision"
                                                data-testid={"revision-task-" + task.id}
                                              >
                                                <RotateCcw className="h-3 w-3" />
                                              </Button>
                                              <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-7 w-7"
                                                onClick={function() { startEditTask(task); }}
                                                data-testid={"edit-task-" + task.id}
                                              >
                                                <Pencil className="h-3 w-3" />
                                              </Button>
                                            </div>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    });
                                    })()}
                                  </TableBody>
                                </Table>
                                </div>
                              ) : (
                                <div className="p-6 text-center text-muted-foreground">
                                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                  <p>No tasks in this group</p>
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No task groups</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg">Timeline</CardTitle></CardHeader>
            <CardContent>
              {taskGroups.length > 0 && months.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  {/* Timeline Legend */}
                  <div className="flex items-center gap-4 px-3 py-2 bg-muted/20 border-b text-xs">
                    <span className="font-medium">Legend:</span>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
                      <span>Today</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
                      <span>Expected (On Schedule)</span>
                    </div>
                  </div>
                  <div className="flex border-b bg-muted/30">
                    <div className="w-44 min-w-44 p-3 border-r text-sm font-medium">Task Group</div>
                    <ScrollArea className="flex-1">
                      <div className="flex relative" style={{ minWidth: "600px" }}>
                        {months.map(function(m, i) {
                          return <div key={i} className="flex-1 p-2 text-center text-xs border-r" style={{ minWidth: "80px" }}>
                            <div className="font-medium">{format(m, "MMM")}</div>
                            <div className="text-muted-foreground">{format(m, "yyyy")}</div>
                          </div>;
                        })}
                        {/* Today marker in header */}
                        {todayPos && (
                          <div className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10" style={{ left: todayPos }} title="Today">
                            <div className="absolute -top-1 -left-1.5 w-3 h-3 bg-red-500 rotate-45"></div>
                          </div>
                        )}
                        {/* Expected position marker in header */}
                        {expectedPos && (
                          <div className="absolute top-0 bottom-0 w-0.5 bg-green-500 z-10" style={{ left: expectedPos }} title="Expected Progress">
                            <div className="absolute -top-1 -left-1.5 w-3 h-3 bg-green-500 rotate-45"></div>
                          </div>
                        )}
                      </div>
                      <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                  </div>
                  <ScrollArea style={{ maxHeight: "400px" }}>
                    {taskGroups.map(function(g) {
                      var gf = g.fields || {};
                      var gn = getFieldSafe(gf, "Task Group Name", "Unnamed");
                      var gs = getFieldSafe(gf, "Task Group Start Date", null);
                      var ge = getFieldSafe(gf, "Task Group Due Date", null);
                      var gp = (getFieldSafe(gf, "Progress %", 0)) * 100;
                      var gst = getFieldSafe(gf, "Progress Status", "Not Started");
                      var cfg = getStatCfg(gst);
                      var bar = calcBar(gs, ge);
                      return (
                        <div key={g.id} className="flex border-b hover:bg-muted/20">
                          <div className="w-44 min-w-44 p-3 border-r"><p className="text-sm font-medium truncate">{gn}</p></div>
                          <ScrollArea className="flex-1">
                            <div className="relative h-12" style={{ minWidth: "600px" }}>
                              <div className="absolute inset-0 flex">
                                {months.map(function(_, i) { return <div key={i} className="flex-1 border-r" style={{ minWidth: "80px" }} />; })}
                              </div>
                              {/* Today marker line through task rows */}
                              {todayPos && (
                                <div className="absolute top-0 bottom-0 w-0.5 bg-red-500/50 z-5" style={{ left: todayPos }}></div>
                              )}
                              {/* Expected position marker line through task rows */}
                              {expectedPos && (
                                <div className="absolute top-0 bottom-0 w-0.5 bg-green-500/50 z-5" style={{ left: expectedPos }}></div>
                              )}
                              {bar && <div className="absolute top-1/2 -translate-y-1/2 h-6 rounded-sm z-10" style={{ left: bar.left, width: bar.width, backgroundColor: cfg.bar, minWidth: "30px" }}>
                                <div className="h-full flex items-center justify-center px-1"><span className="text-xs text-white font-medium">{gp.toFixed(0)}%</span></div>
                              </div>}
                            </div>
                            <ScrollBar orientation="horizontal" />
                          </ScrollArea>
                        </div>
                      );
                    })}
                  </ScrollArea>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mb-4 opacity-30" />
                  <p>No timeline data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p className="text-lg font-medium">No project selected</p>
            <p className="text-sm">Select a project from the dropdown</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Project Dialog */}
      <Dialog open={editingProject} onOpenChange={setEditingProject}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>Update project details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project Name</Label>
                <Input 
                  value={projectForm.name || ""} 
                  onChange={function(e) { setProjectForm({...projectForm, name: e.target.value}); }}
                />
              </div>
              <div className="space-y-2">
                <Label>Customer</Label>
                <Select value={projectForm.company_id || ""} onValueChange={function(v) { setProjectForm({...projectForm, company_id: v}); }}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {companies.map(function(c) { 
                      var companyName = c.fields ? c.fields["Company Name"] : c.name || "Unknown";
                      return <SelectItem key={c.id} value={c.id}>{companyName}</SelectItem>; 
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={projectForm.description || ""} 
                onChange={function(e) { setProjectForm({...projectForm, description: e.target.value}); }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={projectForm.status} onValueChange={function(v) { setProjectForm({...projectForm, status: v}); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROJECT_STATUSES.map(function(s) { return <SelectItem key={s} value={s}>{s}</SelectItem>; })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>OSR (Outside Sales Rep)</Label>
                <MultiSelect
                  options={osrList.map(function(person) { return { value: person.id, label: person.name }; })}
                  selected={projectForm.osr_ids || []}
                  onChange={function(ids) { setProjectForm({...projectForm, osr_ids: ids}); }}
                  placeholder="Select OSR(s)"
                  data-testid="osr-select"
                />
              </div>
              <div className="space-y-2">
                <Label>ISR (Inside Sales Rep)</Label>
                <MultiSelect
                  options={isrList.map(function(person) { return { value: person.id, label: person.name }; })}
                  selected={projectForm.isr_ids || []}
                  onChange={function(ids) { setProjectForm({...projectForm, isr_ids: ids}); }}
                  placeholder="Select ISR(s)"
                  data-testid="isr-select"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input 
                  type="date" 
                  value={projectForm.start_date || ""} 
                  onChange={function(e) { setProjectForm({...projectForm, start_date: e.target.value}); }}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input 
                  type="date" 
                  value={projectForm.end_date || ""} 
                  onChange={function(e) { setProjectForm({...projectForm, end_date: e.target.value}); }}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={projectForm.notes || ""} 
                onChange={function(e) { setProjectForm({...projectForm, notes: e.target.value}); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setEditingProject(false); }}>Cancel</Button>
            <Button onClick={saveProject}><Save className="h-4 w-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Group Dialog */}
      <Dialog open={!!editingTaskGroup} onOpenChange={function(open) { if (!open) setEditingTaskGroup(null); }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Edit Task Group</DialogTitle>
            <DialogDescription>Update task group details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input 
                value={taskGroupForm.name || ""} 
                onChange={function(e) { setTaskGroupForm({...taskGroupForm, name: e.target.value}); }}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={taskGroupForm.status} onValueChange={function(v) { setTaskGroupForm({...taskGroupForm, status: v}); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_GROUP_STATUSES.map(function(s) { return <SelectItem key={s} value={s}>{s}</SelectItem>; })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input 
                  type="date" 
                  value={taskGroupForm.start_date || ""} 
                  onChange={function(e) { setTaskGroupForm({...taskGroupForm, start_date: e.target.value}); }}
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input 
                  type="date" 
                  value={taskGroupForm.due_date || ""} 
                  onChange={function(e) { setTaskGroupForm({...taskGroupForm, due_date: e.target.value}); }}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setEditingTaskGroup(null); }}>Cancel</Button>
            <Button onClick={saveTaskGroup}><Save className="h-4 w-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={!!editingTask} onOpenChange={function(open) { if (!open) setEditingTask(null); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>Update task details, dependencies, and view change history</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="dependencies" className="flex items-center gap-1">
                <Link2 className="h-3 w-3" /> Dependencies
              </TabsTrigger>
              <TabsTrigger value="changelog" className="flex items-center gap-1">
                <History className="h-3 w-3" /> History
              </TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Task Name</Label>
                <Input 
                  value={taskForm.title || ""} 
                  onChange={function(e) { setTaskForm({...taskForm, title: e.target.value}); }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={taskForm.status} onValueChange={function(v) { setTaskForm({...taskForm, status: v}); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TASK_STATUSES.map(function(s) { return <SelectItem key={s} value={s}>{s}</SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Duration (days)</Label>
                  <Input 
                    type="number" 
                    value={taskForm.duration || 0} 
                    onChange={function(e) { setTaskForm({...taskForm, duration: parseInt(e.target.value) || 0}); }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input 
                    type="date" 
                    value={taskForm.start_date || ""} 
                    onChange={function(e) { setTaskForm({...taskForm, start_date: e.target.value}); }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input 
                    type="date" 
                    value={taskForm.due_date || ""} 
                    onChange={function(e) { setTaskForm({...taskForm, due_date: e.target.value}); }}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <Checkbox 
                  id="dates-locked-detail" 
                  checked={taskForm.dates_locked || false}
                  onCheckedChange={function(checked) { setTaskForm({...taskForm, dates_locked: checked}); }}
                />
                <div className="grid gap-0.5 leading-none">
                  <Label htmlFor="dates-locked-detail" className="text-sm font-medium cursor-pointer flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Lock Dates
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Prevent date cascade from changing this task's dates
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Input 
                  value={taskForm.role || ""} 
                  onChange={function(e) { setTaskForm({...taskForm, role: e.target.value}); }}
                />
              </div>
              <div className="space-y-2">
                <Label>Assigned To</Label>
                <MultiSelect
                  options={[
                    ...osrList.map(function(p) { return { value: p.id, label: p.name + " (OSR)" }; }),
                    ...isrList.map(function(p) { return { value: p.id, label: p.name + " (ISR)" }; })
                  ]}
                  selected={taskForm.assigned_to || []}
                  onChange={function(selected) { setTaskForm({...taskForm, assigned_to: selected}); }}
                  placeholder="Select assignees..."
                />
              </div>
            </TabsContent>
            <TabsContent value="dependencies" className="py-4">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Link2 className="h-4 w-4" /> Predecessors (Must Complete First)
                  </Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    This task cannot start until all selected predecessor tasks are completed.
                  </p>
                  {loadingPredecessors ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <ScrollArea className="h-[250px] rounded-md border p-2">
                      {availablePredecessors.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-8">
                          <Link2 className="h-8 w-8 mb-2 opacity-50" />
                          <p className="text-sm">No available predecessor tasks</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {availablePredecessors.map(function(pred) {
                            var isSelected = (taskForm.predecessors || []).includes(pred.id);
                            var isCompletedPred = pred.status === "Completed" || pred.status === "Complete";
                            return (
                              <div
                                key={pred.id}
                                className={"flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors " + (isSelected ? "bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800" : "")}
                                onClick={function() {
                                  var currentPreds = taskForm.predecessors || [];
                                  if (isSelected) {
                                    setTaskForm({...taskForm, predecessors: currentPreds.filter(function(id) { return id !== pred.id; })});
                                  } else {
                                    setTaskForm({...taskForm, predecessors: [...currentPreds, pred.id]});
                                  }
                                }}
                              >
                                <Checkbox checked={isSelected} className="pointer-events-none" />
                                <span className="text-xs text-muted-foreground w-6 flex-shrink-0">#{pred.order || 0}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={"text-sm truncate " + (isCompletedPred ? "line-through text-muted-foreground" : "")}>
                                      {pred.name}
                                    </span>
                                    {isCompletedPred && <Badge variant="outline" className="text-xs bg-green-100 text-green-700">Done</Badge>}
                                  </div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {pred.project ? pred.project + " - " : ""}{pred.task_group || "No group"} - Due: {pred.due_date || "Not set"}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>
                  )}
                  {(taskForm.predecessors || []).length > 0 && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-md">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <strong>{(taskForm.predecessors || []).length}</strong> predecessor(s) selected. 
                        This task will be blocked until they complete.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="changelog" className="py-4">
              <ScrollArea className="h-[300px] rounded-md border">
                {loadingChangelog ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : taskChangelog.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <History className="h-8 w-8 mb-2" />
                    <p>No changes recorded yet</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {taskChangelog.map(function(change, idx) {
                      var changeDate = change.changed_at ? new Date(change.changed_at) : null;
                      var formattedDate = changeDate ? format(changeDate, "MMM d, yyyy h:mm a") : "Unknown";
                      var changeTypeColors = {
                        "status_change": "bg-blue-100 text-blue-700",
                        "assignment": "bg-purple-100 text-purple-700",
                        "date_change": "bg-amber-100 text-amber-700",
                        "update": "bg-gray-100 text-gray-700"
                      };
                      var badgeClass = changeTypeColors[change.change_type] || changeTypeColors.update;
                      
                      return (
                        <div key={idx} className="border-l-2 border-muted pl-3 pb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className={badgeClass + " text-xs"}>
                              {change.field_name}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{formattedDate}</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground line-through mr-2">{change.old_value || "(empty)"}</span>
                            <span className="text-foreground">→ {change.new_value || "(empty)"}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            by {change.changed_by}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setEditingTask(null); }}>Cancel</Button>
            <Button onClick={saveTask}><Save className="h-4 w-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revision Request Dialog */}
      <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-500" />
              Request Revision
            </DialogTitle>
            <DialogDescription>
              {revisionTask && (
                <span>Task: <strong>{revisionTask.fields?.["Task Name"] || "Untitled"}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Days to Add *</Label>
              <Input
                type="number"
                min="1"
                value={revisionForm.days_added}
                onChange={function(e) { setRevisionForm({...revisionForm, days_added: parseInt(e.target.value) || 1}); }}
                data-testid="revision-days-input"
              />
              <p className="text-xs text-muted-foreground">This will extend the due date and push dependent tasks</p>
            </div>
            <div className="space-y-2">
              <Label>Requested By *</Label>
              <Select 
                value={revisionForm.requested_by} 
                onValueChange={function(v) { setRevisionForm({...revisionForm, requested_by: v}); }}
              >
                <SelectTrigger data-testid="revision-requester-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REVISION_REQUESTERS.map(function(r) {
                    return <SelectItem key={r} value={r}>{r}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea
                value={revisionForm.reason}
                onChange={function(e) { setRevisionForm({...revisionForm, reason: e.target.value}); }}
                placeholder="Describe why this revision is needed..."
                rows={3}
                data-testid="revision-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowRevisionDialog(false); }}>Cancel</Button>
            <Button 
              onClick={submitRevision} 
              disabled={submittingRevision || !revisionForm.reason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-revision-btn"
            >
              {submittingRevision && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <RotateCcw className="h-4 w-4 mr-1" /> Request Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revision History Dialog */}
      <Dialog open={showRevisionHistory} onOpenChange={setShowRevisionHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-amber-500" />
              Revision History
            </DialogTitle>
            {revisionHistory && (
              <DialogDescription>
                Task: <strong>{revisionHistory.task_name}</strong>
                <span className="ml-4">Total: <strong>+{revisionHistory.total_revision_days} days</strong> ({revisionHistory.revision_count} revision{revisionHistory.revision_count !== 1 ? 's' : ''})</span>
              </DialogDescription>
            )}
          </DialogHeader>
          {revisionHistory && revisionHistory.revisions.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Days Added</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {revisionHistory.revisions.map(function(rev, idx) {
                    return (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{rev.revision_number}</TableCell>
                        <TableCell className="text-sm">{formatDateSafe(rev.requested_date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{rev.requested_by}</Badge>
                        </TableCell>
                        <TableCell className="font-medium text-amber-600">+{rev.days_added} days</TableCell>
                        <TableCell className="text-sm max-w-xs truncate" title={rev.reason}>{rev.reason}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">No revision history</p>
          )}
          {revisionHistory && (
            <div className="mt-4 p-3 bg-muted rounded-lg text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>Original Due Date: <strong>{formatDateSafe(revisionHistory.original_due_date)}</strong></div>
                <div>Current Due Date: <strong>{formatDateSafe(revisionHistory.current_due_date)}</strong></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dependency Impact Warning Dialog */}
      <AlertDialog open={dependencyImpactDialog.open} onOpenChange={function(open) { if (!open) setDependencyImpactDialog({ open: false, dependentTasks: [], pendingTaskUpdate: null }); }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-amber-500" />
              Dependent Tasks Found
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  The following {dependencyImpactDialog.dependentTasks.length} task(s) depend on this task and may need their dates adjusted:
                </p>
                <div className="max-h-40 overflow-y-auto rounded-md border p-2 bg-muted/30">
                  {dependencyImpactDialog.dependentTasks.map(function(dep, idx) {
                    return (
                      <div key={idx} className="flex items-center justify-between py-1 text-sm">
                        <span className="truncate flex-1">{dep.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {dep.current_start}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-sm font-medium">
                  Would you like to shift the dependent tasks' start dates to follow the new due date?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={function() { setDependencyImpactDialog({ open: false, dependentTasks: [], pendingTaskUpdate: null }); }}>
              Cancel
            </AlertDialogCancel>
            <Button variant="outline" onClick={function() { handleDependencyImpactConfirm(false); }}>
              Save Without Shifting
            </Button>
            <Button onClick={function() { handleDependencyImpactConfirm(true); }} className="bg-amber-600 hover:bg-amber-700">
              Shift Dependent Tasks
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ProjectDetailPage;
